import { type LocaleState } from "@/app/store";

import { type AddToCartSource } from "@/app/components/shared/recommended-products/add-to-cart-event";
import {
	type AddToCartEvent,
	type AnalyticsEvent,
	type PdpContentClickEvent,
	type ViewItemEvent,
	type ViewItemListEvent
} from "./analytics-events";
import {
	getCommonAnalytics,
	getDataLayer,
	onAnalyticsReady
} from "./common-analytics";

const VIEW_ITEM_LIST_NAME = "PDP";

const CURRENCY_BY_LOCALE: Record<LocaleState, string> = {
	"en-US": "USD",
	"en-CA": "CAD"
};

// Strings mirror the legacy Hybris PDP dataLayer so GA4 reporting stays
// continuous across the migration (verified against test Hybris). atc/cart
// surfaces are not on the PDP yet — those values are unverified against Hybris.
const PRODUCT_FINDING_METHOD_BY_SOURCE: Record<AddToCartSource, string> = {
	pdp: "PDP",
	"pdp-recommended": "product_recommendations_pdp",
	"atc-recommended": "product_recommendations_a2c",
	"cart-recommended": "product_recommendations_cart",
	"recently-viewed": "PDP"
};

const ITEM_LIST_NAME_BY_SOURCE: Record<AddToCartSource, string> = {
	pdp: "product detail page",
	"pdp-recommended": "PDP Product Recommendation",
	"atc-recommended": "A2C Product Recommendations",
	"cart-recommended": "Cart Product Recommendations",
	"recently-viewed": "Recently Viewed"
};

/**
 * Global, typed entry point for the analytics events consumer-web emits.
 * `track` is the single chokepoint: it SSR-guards, waits for common-analytics
 * to load, then routes the typed event to the matching common-analytics call.
 * Add a new `case` here when extending `AnalyticsEvent`.
 */
export const analyticsManager = {
	track(event: AnalyticsEvent): void {
		if (typeof window === "undefined") return;
		onAnalyticsReady(() => dispatch(event));
	}
};

function dispatch(event: AnalyticsEvent): void {
	switch (event.type) {
		case "add_to_cart":
			trackAddToCart(event);
			break;
		case "view_item":
			trackViewItem(event);
			break;
		case "view_item_list":
			trackViewItemList(event);
			break;
		case "pdp_content_click":
			trackPdpContentClick(event);
			break;
	}
}

function trackAddToCart(event: AddToCartEvent): void {
	const addToCart = getCommonAnalytics()?.events.addToCart;
	if (typeof addToCart !== "function") return;

	addToCart(
		CURRENCY_BY_LOCALE[event.locale],
		[{ id: event.sku, quantity: event.quantity, price: event.price }],
		ITEM_LIST_NAME_BY_SOURCE[event.source],
		PRODUCT_FINDING_METHOD_BY_SOURCE[event.source]
	);
}

// A PDP view fans out to independent sinks: GA4 (via common-analytics
// `viewItem`) and user-analytics (`/collect` via the datalayer's
// `product-viewed` command, which also sets PC_DEVICE_ID for legacy parity).
// Each sink is guarded and swallows failure so one missing/failing sink cannot
// break the other or the PDP render.
function trackViewItem(event: ViewItemEvent): void {
	const viewItem = getCommonAnalytics()?.events.viewItem;
	if (typeof viewItem === "function") {
		Promise.resolve(viewItem(VIEW_ITEM_LIST_NAME, [event.sku])).catch(
			() => {}
		);
	}

	Promise.resolve(
		getDataLayer()?.addInformation({
			type: "product-viewed",
			sku: event.sku
		})
	).catch(() => {});
}

function trackPdpContentClick(event: PdpContentClickEvent): void {
	const pdpContentClick = getCommonAnalytics()?.events.pdpContentClick;
	if (typeof pdpContentClick !== "function") return;

	Promise.resolve(
		pdpContentClick({
			actionName: event.actionName,
			productSku: event.sku,
			currency: CURRENCY_BY_LOCALE[event.locale],
			assetId: event.assetId,
			position: event.position
		})
	).catch(() => {});
}

function trackViewItemList(event: ViewItemListEvent): void {
	const viewItemList = getCommonAnalytics()?.events.viewItemList;
	if (typeof viewItemList !== "function" || event.skus.length === 0) return;

	Promise.resolve(viewItemList(event.itemListName, event.skus)).catch(
		() => {}
	);
}
